import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CtaButton } from "@/components/CtaButton";
import { GhlEmbedPlaceholder } from "@/components/GhlEmbedPlaceholder";
import { SITE } from "@/constants/testIds";

export default function PackagesPage() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.packages().then(setItems).catch(() => {});
    }, []);

    return (
        <div>
            <PageHeader
                eyebrow="PACKAGES & CONSULTATIONS"
                title="Plans that meet you where you are"
                subtitle="From a single consultation to a 30-day deep reset — transparent pricing, generous time, and personal care."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Packages" }]}
            />

            <section data-testid={SITE.pricingSection} className="py-12 sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        {items.map((p, i) => (
                            <article
                                key={p.slug}
                                data-testid={SITE.pricingPlan}
                                data-slug={p.slug}
                                className={`relative rounded-3xl border bg-white/85 p-7 flex flex-col ${
                                    i === 1
                                        ? "border-terracotta/55 shadow-[0_24px_60px_rgba(200,107,74,0.16)]"
                                        : "border-border/80"
                                }`}
                            >
                                {i === 1 && (
                                    <span className="absolute -top-3 left-7 inline-flex items-center rounded-full bg-terracotta px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wider text-cream shadow-sm">
                                        Most chosen
                                    </span>
                                )}
                                <span className="inline-flex w-fit rounded-full bg-sand px-3 py-1 text-xs text-moss">
                                    {p.badge}
                                </span>
                                <h2 className="mt-4 font-display text-2xl text-moss">{p.name}</h2>
                                <p className="mt-1 text-sm text-foreground/70 leading-relaxed">
                                    {p.description}
                                </p>
                                <div className="mt-5">
                                    <span className="font-display text-4xl text-moss">{p.price}</span>
                                </div>
                                <ul className="mt-5 space-y-2.5 text-sm text-foreground/85">
                                    {p.includes?.map((inc, k) => (
                                        <li key={k} className="flex items-start gap-2">
                                            <Check className="mt-0.5 h-4 w-4 text-sage shrink-0" strokeWidth={2} />
                                            <span>{inc}</span>
                                        </li>
                                    ))}
                                </ul>
                                {p.best_for && (
                                    <p className="mt-5 rounded-xl bg-sand/60 border border-border/70 p-3 text-xs text-foreground/75">
                                        <span className="font-accent text-terracotta">BEST FOR</span> · {p.best_for}
                                    </p>
                                )}
                                <div className="mt-6 pt-2">
                                    <CtaButton
                                        to="/contact"
                                        variant={i === 1 ? "accent" : "primary"}
                                        testid={SITE.pricingCta}
                                        className="w-full"
                                    >
                                        Request this package
                                    </CtaButton>
                                </div>
                            </article>
                        ))}
                    </div>

                    <div className="mt-10 rounded-2xl bg-cream border border-border/80 p-6 text-sm text-foreground/75">
                        <p>
                            <span className="font-accent text-xs text-terracotta">A NOTE</span> ·
                            All packages can be combined with bodywork treatments (Abhyanga, Shirodhara, Svedana) at our Makawao clinic. Riley will recommend the right combination during your intake.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 sand-wash bg-grain">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        <div className="lg:col-span-5">
                            <h3 className="font-display text-2xl text-moss">
                                Have questions about a package?
                            </h3>
                            <p className="mt-2 text-foreground/75">
                                Send us a note and we’ll help you choose the right starting point.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <GhlEmbedPlaceholder kind="consultation-form" />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
